import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Restaurant } from '../models/Restaurant';

interface WinnerModalProps {
  restaurant: Restaurant | null;
  isOpen: boolean;
  onClose: () => void;
}

const WinnerModal: React.FC<WinnerModalProps> = ({ restaurant, isOpen, onClose }) => {
  const [expandDetails, setExpandDetails] = useState(false);

  if (!restaurant) return null;

  const toggleDetails = () => {
    setExpandDetails(!expandDetails);
  };

  const handleViewDirections = () => {
    if (restaurant) {
      const address = encodeURIComponent(restaurant.location.display_address.join(', '));
      window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
    }
  };

  const handleViewWebsite = () => {
    if (restaurant) {
      window.open(restaurant.url, '_blank');
    }
  };

  const formatCategory = (categories: { alias: string; title: string }[]) => {
    return categories.map(category => category.title).join(', ');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContainer
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 20 }}
            onClick={e => e.stopPropagation()}
          >
            <CloseButton onClick={onClose}>&times;</CloseButton>
            <ModalHeader>
              <WinnerTitle>You're going to</WinnerTitle>
              <RestaurantName>{restaurant.name}</RestaurantName>
            </ModalHeader>

            <ImageContainer>
              <RestaurantImage 
                src={restaurant.image_url} 
                alt={restaurant.name} 
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=No+Image+Available';
                }}
              />
              <ImageOverlay>
                <PriceRating>{restaurant.price}</PriceRating>
                <StarRating>★ {restaurant.rating} ({restaurant.review_count} reviews)</StarRating>
              </ImageOverlay>
            </ImageContainer>

            <InfoSection>
              <InfoItem>
                <InfoLabel>Category</InfoLabel>
                <InfoValue>{formatCategory(restaurant.categories)}</InfoValue>
              </InfoItem>
              
              <InfoItem>
                <InfoLabel>Address</InfoLabel>
                <InfoValue>{restaurant.location.display_address.join(', ')}</InfoValue>
              </InfoItem>
              
              <InfoItem>
                <InfoLabel>Phone</InfoLabel>
                <InfoValue>{restaurant.display_phone || 'N/A'}</InfoValue>
              </InfoItem>
            </InfoSection>

            {expandDetails && (
              <MapContainer>
                <iframe
                  width="100%"
                  height="200"
                  frameBorder="0"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAhhqB3VAXJKiu8TdjGrIY0wbcIC9wXn9c&q=${encodeURIComponent(
                    restaurant.location.display_address.join(', ')
                  )}`}
                  allowFullScreen
                ></iframe>
              </MapContainer>
            )}

            <ButtonsContainer>
              <ExpandButton onClick={toggleDetails}>
                {expandDetails ? 'Hide Details' : 'Show Details'}
              </ExpandButton>
              <ActionButton onClick={handleViewDirections}>
                Get Directions
              </ActionButton>
              <ActionButton onClick={handleViewWebsite}>
                Visit Website
              </ActionButton>
            </ButtonsContainer>
          </ModalContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 500px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  padding: 1.5rem 1rem;
  background: var(--primary-color);
  color: white;
`;

const WinnerTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0;
  opacity: 0.8;
`;

const RestaurantName = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0.5rem 0 0;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
`;

const RestaurantImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 10px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
`;

const PriceRating = styled.div`
  font-weight: bold;
`;

const StarRating = styled.div`
  font-weight: bold;
`;

const InfoSection = styled.div`
  padding: 1.5rem;
`;

const InfoItem = styled.div`
  margin-bottom: 1rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-size: 1.1rem;
  color: #333;
`;

const MapContainer = styled.div`
  width: 100%;
  padding: 0 1.5rem 1.5rem;
`;

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding: 0 1.5rem 1.5rem;
`;

const ActionButton = styled.button`
  padding: 12px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  grid-column: span 1;
  
  &:hover {
    background: var(--accent-color);
  }
`;

const ExpandButton = styled.button`
  padding: 12px;
  background: #f2f2f2;
  color: #333;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  grid-column: span 2;
  
  &:hover {
    background: #e0e0e0;
  }
`;

export default WinnerModal;